import { CompanyMeasurementDateName } from 'api';
import { FundByCompanyRowGroup } from 'pages/Firms/hooks';
import { getNumberValue, getSlugValue } from 'utilities';

// Titles
export const FIRM_SUMMARY_TITLE = 'Firm Summary';
export const FIRM_SUMMARY_KEY = getSlugValue(FIRM_SUMMARY_TITLE);
export const FIRM_SUMMARY_CONTAINER_CLASSNAME = `${FIRM_SUMMARY_KEY}-container`;
export const FIRM_SUMMARY_TOTAL_TITLE = 'Total';
export const FIRM_SUMMARY_DRAFT_TITLE = 'DRAFT';
export const FIRM_SUMMARY_VALUES_SHOWN_PUBLISHED_TITLE = 'Summary values shown for published valuations';
export const FIRM_SUMMARY_VALUE_WITH_NOT_PUBLISHED_VALUATION_TOOLTIP =
  'This value isn’t shown until the valuation allocation for this company is published';
export const FIRM_SUMMARY_ADD_COMPANY_TITLE = 'Add Company';
export const FIRM_SUMMARY_PROCESS_MANAGEMENT_COLUMN_TYPE = 'process_management';

// Measurement date
export const FIRM_SUMMARY_MEASUREMENT_DATES_TITLE = 'Measurement Dates';
export const FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE_TITLE = 'Most Recent';
export const FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE_SLUG = getSlugValue(
  FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE_TITLE
);
export const FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE: CompanyMeasurementDateName = Object.freeze({
  date: FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE_SLUG,
  id: Number.MAX_SAFE_INTEGER,
  is_open: false,
  md_id: Number.MAX_SAFE_INTEGER,
  company_id: 0,
  company_name: '',
  is_ready: false,
  name: FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE_TITLE,
  slug: '', // Empty slug removes date query param
});

// Aliases
export const FIRM_SUMMARY_SPREADSHEET_TITLE_ALIAS = 'FIRM_SUMMARY_SPREADSHEET_TITLE';
export const FIRM_SUMMARY_SPREADSHEET_TOTAL_ALIAS = 'FIRM_SUMMARY_SPREADSHEET_TOTAL';

// Spreadsheet
export const FIRM_SUMMARY_SPREADSHEET_COLUMN_NAME = 'Column';
export const FIRM_SUMMARY_SPREADSHEET_COLUMN_NAME_PLURAL = 'Columns';
export const FIRM_SUMMARY_SPREADSHEET_TITLE = 'Firm Portfolio Summary';
export const FIRM_SUMMARY_SPREADSHEET_SLUG = getSlugValue(FIRM_SUMMARY_SPREADSHEET_TITLE);
export const FIRM_SUMMARY_SPREADSHEET_NAME = `${FIRM_SUMMARY_SPREADSHEET_SLUG}-spreadsheet`;

export const FIRM_SUMMARY_SPREADSHEET_TABLE_TERMS = Object.freeze({
  columnName: FIRM_SUMMARY_SPREADSHEET_COLUMN_NAME,
  pluralColumnName: FIRM_SUMMARY_SPREADSHEET_COLUMN_NAME_PLURAL,
  tableName: FIRM_SUMMARY_SPREADSHEET_NAME,
  tableSlug: FIRM_SUMMARY_SPREADSHEET_SLUG,
});

// Columns
export const FIRM_SUMMARY_SPREADSHEET_COLUMNS = Object.freeze({
  company: {
    column: 0,
    title: 'Company',
    tooltip: '',
  },
  fund: {
    column: 1,
    title: 'Fund',
    tooltip: '',
  },
  valuation_date: {
    column: 2,
    title: 'Valuation Date',
    tooltip: '',
  },
  initial_investment_date: {
    column: 3,
    title: 'Initial Investment Date',
    tooltip: '',
  },
  most_recent_investment_date: {
    column: 4,
    title: 'Most Recent Investment Date',
    tooltip: '',
  },
  fully_diluted_ownership_percentage: {
    column: 5,
    title: 'Fully Diluted Ownership %',
    tooltip: '',
  },
  invested_capital: {
    column: 6,
    title: 'Invested Capital',
    tooltip: '',
  },
  realized_value: {
    column: 7,
    title: 'Realized Value',
    tooltip: '',
  },
  unrealized_value: {
    column: 8,
    title: 'Unrealized Value',
    tooltip: '',
  },
  total_value: {
    column: 9,
    title: 'Total Value',
    tooltip: '',
  },
  gross_irr: {
    column: 10,
    title: 'Gross IRR',
    tooltip: '',
  },
  moic: {
    column: 11,
    title: 'MOIC',
    tooltip: '',
  },
});

export const getFirmSummaryColumn = (columnNumber: number) => {
  const columnsArray = Object.values(FIRM_SUMMARY_SPREADSHEET_COLUMNS);

  return columnsArray?.[columnNumber];
};

// Default values
export const FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS = 'All Funds';
export const FIRM_SUMMARY_SPREADSHEET_DEFAULT_CURRENCY = 'USD';
export const FIRM_SUMMARY_SPREADSHEET_ALLOW_NEGATIVE_VALUE = true;
export const FIRM_SUMMARY_SPREADSHEET_ROW_SPAN = 1;
export const FIRM_SUMMARY_SPREADSHEET_INDENTED_CLASSNAME = 'subtitle-indented';
export const FIRM_SUMMARY_SPREADSHEET_TOOLTIP_CLASSNAME = 'show-tooltip';
export const FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX = getSlugValue(FIRM_SUMMARY_SPREADSHEET_TITLE);
export const FIRM_SUMMARY_SPREADSHEET_EXCLUDED_ROWS = Object.freeze([FIRM_SUMMARY_SPREADSHEET_TITLE_ALIAS]);
export const FIRM_SUMMARY_SPREADSHEET_EXCLUDED_ROWS_COUNT = getNumberValue(
  FIRM_SUMMARY_SPREADSHEET_EXCLUDED_ROWS.length
);
export const FIRM_SUMMARY_SPREADSHEET_NON_PARENT_ROW_GROUP: FundByCompanyRowGroup = Object.freeze({
  funds_count: 0,
  is_parent: false,
  parent_key: null,
});
